/**
 * What readers have said, as shown on the website.
 *
 * One document at content/reviews holding the whole list. The site reads it
 * with a single public GET and no Firebase SDK, so publishing a review is a
 * write here and nothing else -- no rebuild, no deploy.
 */
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { COLLECTIONS, REVIEWS_DOC } from '@dnyaneshwari/shared';
import type { Review, ReviewsFile } from '@dnyaneshwari/shared';
import { db } from './firebase';

export const SOURCES = ['whatsapp', 'playstore', 'appstore', 'email', 'person', 'other'] as const;
export type Source = (typeof SOURCES)[number];

export const SOURCE_LABEL: Record<Source, string> = {
  whatsapp: 'WhatsApp',
  playstore: 'Play Store',
  appstore: 'App Store',
  email: 'Email',
  person: 'In person',
  other: 'Other',
};

export async function getReviews(): Promise<Review[]> {
  const snap = await getDoc(doc(db, COLLECTIONS.CONTENT, REVIEWS_DOC));
  if (!snap.exists()) return [];
  return ((snap.data() as ReviewsFile).reviews ?? []).slice();
}

/**
 * Writes the whole list. Reviews are a handful of short rows that the admin
 * reorders by hand, so the document is replaced rather than patched -- there
 * is no second writer to race with, and a whole-document write keeps the
 * order on screen and the order on the site the same thing.
 */
export async function saveReviews(reviews: Review[]): Promise<void> {
  const file: ReviewsFile = { version: new Date().toISOString(), reviews };
  await setDoc(doc(db, COLLECTIONS.CONTENT, REVIEWS_DOC), file);
}

export function blankReview(): Review {
  return {
    id: `rv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    place: '',
    source: 'whatsapp',
    rating: 5,
    text: '',
    textEn: '',
    receivedAt: new Date().toISOString().slice(0, 10),
    published: true,
  };
}

/** Drops the empty optional fields, so the document stays readable. */
export function tidy(r: Review): Review {
  const out: Review = {
    id: r.id,
    name: r.name.trim(),
    text: r.text.trim(),
    published: r.published,
  };
  if (r.place?.trim()) out.place = r.place.trim();
  if (r.source) out.source = r.source;
  if (r.rating && r.rating > 0) out.rating = r.rating;
  if (r.textEn?.trim()) out.textEn = r.textEn.trim();
  if (r.receivedAt) out.receivedAt = r.receivedAt;
  return out;
}
